import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaBuilding,
  FaFileInvoice,
  FaQuoteRight,
  FaQuestionCircle,
  FaUserPlus,
  FaExternalLinkAlt,
  FaMoneyBillWave,
  FaPaperPlane,
} from "react-icons/fa";
import api from "../../api/axios";
import "./WhatsAppChat.css";

const UserInfoSidebar = ({ activeConversation, onSelectQuickMessage, onInsertQuickMessage }) => {
  const navigate = useNavigate();
  const [crmSummary, setCrmSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [quickMessages, setQuickMessages] = useState([]);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);

  // Fetch CRM Summary whenever active conversation changes
  useEffect(() => {
    if (!activeConversation?.id) {
      setCrmSummary(null);
      return;
    }

    setIsLoadingSummary(true);
    api.get(`/whatsapp/conversations/${activeConversation.id}/crm-summary`)
      .then((res) => {
        setCrmSummary(res.data);
      })
      .catch((err) => {
        console.error("Error fetching CRM summary:", err);
      })
      .finally(() => {
        setIsLoadingSummary(false);
      });
  }, [activeConversation]);

  // Fetch Quick Messages / Canned Responses
  useEffect(() => {
    api.get("/whatsapp/quick-messages")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setQuickMessages(res.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching quick messages:", err);
      });
  }, []);

  const contactName =
    crmSummary?.customer?.full_name ||
    activeConversation?.contact?.name ||
    activeConversation?.contact?.phone ||
    "Contact";
  const contactPhone =
    crmSummary?.customer?.phone_number ||
    activeConversation?.contact?.phone ||
    "";
  const isLinked = crmSummary?.is_linked;
  const customer = crmSummary?.customer;

  // 1-Click Convert Lead to Rekonic Customer
  const handleConvertToCustomer = async () => {
    if (!activeConversation?.id) return;
    setIsCreatingCustomer(true);
    try {
      const res = await api.post(`/whatsapp/conversations/${activeConversation.id}/create-customer`, {
        full_name: contactName,
        email: "",
      });
      alert(`Customer ${res.data.customer_name} created successfully! (Code: ${res.data.customer_code})`);
      // Refresh CRM Summary
      const summaryRes = await api.get(`/whatsapp/conversations/${activeConversation.id}/crm-summary`);
      setCrmSummary(summaryRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create customer");
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  // Navigations with prefilled customer state
  const handleCreateQuotation = () => {
    navigate("/dashboard/quotations/new", {
      state: {
        customer_name: contactName,
        phone: contactPhone,
      },
    });
  };

  const handleCreateInvoice = () => {
    navigate("/dashboard/sales/new", {
      state: {
        customer_name: contactName,
        customer_phone: contactPhone,
        customer_id: customer?.id,
      },
    });
  };

  const handleCreateInquiry = () => {
    navigate("/dashboard/inquiries/new", {
      state: {
        customer_name: contactName,
        phone: contactPhone,
      },
    });
  };

  const handleViewCustomerProfile = () => {
    if (customer?.id) {
      navigate(`/dashboard/customers/${customer.id}`);
    }
  };

  if (!activeConversation) {
    return (
      <div className="rekonic-crm-sidebar" style={{ padding: "20px", color: "#64748b", textAlign: "center" }}>
        Select a conversation to inspect CRM customer info.
      </div>
    );
  }

  return (
    <div className="rekonic-crm-sidebar">
      {/* Sidebar Header */}
      <div className="rekonic-crm-sidebar-header">
        <h3 className="rekonic-crm-sidebar-title">Rekonic CRM Profile</h3>
        {isLinked ? (
          <span className="rekonic-crm-badge">Linked Customer</span>
        ) : (
          <span className="rekonic-lead-badge">Unlinked Lead</span>
        )}
      </div>

      {/* Customer Hero Card */}
      <div className="rekonic-crm-card">
        <div className="rekonic-customer-hero">
          <div className={`rekonic-hero-avatar ${isLinked ? "" : "unlinked"}`}>
            {contactName.slice(0, 2).toUpperCase()}
          </div>
          <h4 className="rekonic-hero-name">{contactName}</h4>
          <p className="rekonic-hero-phone">{contactPhone}</p>
        </div>

        {/* If Linked CRM Customer Details */}
        {isLinked && customer ? (
          <>
            <div className="rekonic-customer-stats-grid">
              <div className="rekonic-stat-box">
                <div className="rekonic-stat-val">
                  {crmSummary?.stats?.total_invoices_count || 0}
                </div>
                <div className="rekonic-stat-lbl">Total Invoices</div>
              </div>
              <div className="rekonic-stat-box">
                <div className="rekonic-stat-val outstanding">
                  AED {(crmSummary?.stats?.total_outstanding_balance || customer.opening_balance || 0).toLocaleString()}
                </div>
                <div className="rekonic-stat-lbl">Outstanding Balance</div>
              </div>
            </div>

            <div style={{ marginTop: "14px" }}>
              <div className="rekonic-detail-row">
                <span className="rekonic-detail-key">Customer Code</span>
                <span className="rekonic-detail-val">{customer.customer_code || "-"}</span>
              </div>
              <div className="rekonic-detail-row">
                <span className="rekonic-detail-key">Email</span>
                <span className="rekonic-detail-val">{customer.email || "-"}</span>
              </div>
              <div className="rekonic-detail-row">
                <span className="rekonic-detail-key">TRN Number</span>
                <span className="rekonic-detail-val">{customer.trn_number || "-"}</span>
              </div>
              <div className="rekonic-detail-row">
                <span className="rekonic-detail-key">Address</span>
                <span className="rekonic-detail-val">{customer.full_address || "-"}</span>
              </div>
            </div>

            <div className="rekonic-crm-actions-bar">
              <button className="rekonic-action-btn" onClick={handleViewCustomerProfile}>
                <FaExternalLinkAlt /> View Customer Profile
              </button>
              <button className="rekonic-action-btn" onClick={handleCreateQuotation}>
                <FaQuoteRight /> + New Quotation
              </button>
              <button className="rekonic-action-btn" onClick={handleCreateInvoice}>
                <FaFileInvoice /> + New Invoice / Sale
              </button>
              <button className="rekonic-action-btn" onClick={handleCreateInquiry}>
                <FaQuestionCircle /> + Log Inquiry
              </button>
            </div>
          </>
        ) : (
          /* Unlinked Lead View */
          <div style={{ textAlign: "center", marginTop: "10px" }}>
            <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>
              This WhatsApp contact is not yet registered as a customer in Rekonic CRM.
            </p>
            <button
              className="rekonic-action-btn btn-convert"
              onClick={handleConvertToCustomer}
              disabled={isCreatingCustomer}
            >
              <FaUserPlus /> {isCreatingCustomer ? "Creating..." : "+ Add to CRM Customers"}
            </button>
            <button
              className="rekonic-action-btn"
              style={{ marginTop: "8px" }}
              onClick={handleCreateInquiry}
            >
              <FaQuestionCircle /> + Log New Inquiry
            </button>
          </div>
        )}
      </div>

      {/* Recent Invoices Section (if any) */}
      {isLinked && crmSummary?.recent_invoices?.length > 0 && (
        <div className="rekonic-accordion-section">
          <h4 className="rekonic-accordion-title">
            <span>Recent Invoices</span>
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              {crmSummary.recent_invoices.length}
            </span>
          </h4>
          {crmSummary.recent_invoices.map((inv) => (
            <div key={inv.id} className="rekonic-mini-item">
              <div className="rekonic-mini-item-left">
                <span className="rekonic-mini-title">{inv.invoice_number || `Invoice #${inv.id}`}</span>
                <span className="rekonic-mini-date">{inv.invoice_date || "Recent"}</span>
              </div>
              <span className="rekonic-mini-amount">AED {inv.total_amount?.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Quotations Section (if any) */}
      {isLinked && crmSummary?.recent_quotations?.length > 0 && (
        <div className="rekonic-accordion-section">
          <h4 className="rekonic-accordion-title">
            <span>Recent Quotations</span>
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              {crmSummary.recent_quotations.length}
            </span>
          </h4>
          {crmSummary.recent_quotations.map((q) => (
            <div key={q.id} className="rekonic-mini-item">
              <div className="rekonic-mini-item-left">
                <span className="rekonic-mini-title">Quote #{q.id}</span>
                <span className="rekonic-mini-date">{q.created_at || "Recent"}</span>
              </div>
              <span className="rekonic-mini-amount">AED {q.total_amount?.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Rekonic CRM Quick Messages / Canned Responses */}
      <div className="rekonic-accordion-section" style={{ flex: 1 }}>
        <h4 className="rekonic-accordion-title">
          <span>CRM Quick Responses</span>
        </h4>
        <div style={{ maxHeight: "320px", overflowY: "auto" }}>
          {quickMessages.map((item, idx) => (
            <div key={idx} className="rekonic-quick-msg-card">
              <div className="rekonic-quick-msg-title">{item.title}</div>
              <div className="rekonic-quick-msg-text">{item.text}</div>
              <div className="rekonic-quick-msg-actions">
                <button
                  className="rekonic-btn-insert"
                  onClick={() => onInsertQuickMessage && onInsertQuickMessage(item.text)}
                  title="Insert into chat box"
                >
                  Insert
                </button>
                <button
                  className="rekonic-btn-insert"
                  style={{ background: "#25d366", color: "#ffffff", borderColor: "#25d366" }}
                  onClick={() => onSelectQuickMessage(item.text)}
                  title="Send immediately"
                >
                  <FaPaperPlane style={{ fontSize: "10px", marginRight: "3px" }} /> Send
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserInfoSidebar;
